/**
 * @file 手牌 / 座位轮转的共享原语。
 *
 * 这些函数被 `game.ts`（状态机）、`ting.ts`（听牌）、`settle.ts`（结算）三处共用。
 * 抽出来的唯一目的是**口径唯一**：`hasQueTile` 这类判定一旦在三个地方各写一遍，
 * 迟早会出现「出牌路径认为已清缺、胡牌路径认为没清缺」的分裂。
 *
 * 纯函数、平台无关。
 */

import type { Meld, PlayerState, SeatIndex, Seats, Suit, TileId } from './types';
import { kindIndexFromTileId, suitToIndex } from './tiles';

/** 手牌 id 列表 → 27 维计数向量。 */
export function handCounts(hand: readonly TileId[]): Uint8Array {
  const counts = new Uint8Array(27);
  for (const id of hand) {
    const k = kindIndexFromTileId(id);
    counts[k] = (counts[k] ?? 0) + 1;
  }
  return counts;
}

/**
 * 手牌中是否仍有定缺花色的牌（GDD 02 §1.5 的 `hasQueTile`）。
 *
 * **只看手牌**：副露中理论上不可能出现缺门牌，因为 QC-2~QC-5 在未清缺时
 * 禁止一切副露。`voidSuit === null`（尚未定缺）时返回 `false`。
 */
export function hasQueTile(player: PlayerState): boolean {
  if (player.voidSuit === null) return false;
  const base = suitToIndex(player.voidSuit) * 9;
  const counts = handCounts(player.hand);
  for (let r = 0; r < 9; r++) if ((counts[base + r] ?? 0) > 0) return true;
  return false;
}

/** 手牌中属于定缺花色的牌 id（QC-1 的合法出牌集合）。 */
export function queTilesOf(player: PlayerState): readonly TileId[] {
  if (player.voidSuit === null) return [];
  const q = suitToIndex(player.voidSuit);
  return player.hand.filter((id) => Math.floor(kindIndexFromTileId(id) / 9) === q);
}

/**
 * 血战到底的轮转：跳过已胡玩家（GDD 02 §0.1 `nextActiveSeat`）。
 *
 * @returns 下一个 `PLAYING` 座位；四家皆已胡（不可能）时返回 `null`
 */
export function nextActiveSeat(players: Seats<PlayerState>, seat: SeatIndex): SeatIndex | null {
  let s = seat;
  for (let i = 0; i < 4; i++) {
    s = ((s + 1) % 4) as SeatIndex;
    if (!players[s].hasWon) return s;
  }
  return null;
}

/** 该座位「完成摸牌后」应有的手牌张数：`3 × (4 − 副露数) + 2`。 */
export function expectedHandSize(melds: readonly Meld[]): number {
  return 3 * (4 - melds.length) + 2;
}

/**
 * 杠上花标志（`02` §4.6）。
 *
 * 引擎不额外存 `flags.gangFlower`——它可以从副露的 `atTurn` 无歧义地推出来：
 * 杠后补摸会把 `turnCount` 推进一格并把该 meld 的 `atTurn` 记为推进后的值，
 * 而玩家一旦出牌，`turnCount` 又会因下一次摸牌继续前进，标志自然失效。
 * 少存一个字段，就少一处「忘了清除」的 bug。
 */
export function isGangFlower(player: PlayerState, turnCount: number): boolean {
  for (const m of player.melds) {
    if (m.type !== 'peng' && m.atTurn === turnCount) return true;
  }
  return false;
}

/**
 * 定缺兜底选择（`02` §1.4 `autoPickQue`）。**必须确定性**：同样手牌必得同样结果，
 * 否则回放不可复现。花色遍历顺序固定为 wan → tong → tiao。
 */
export function autoPickQue(hand: readonly TileId[]): Suit {
  const counts = handCounts(hand);
  let bestSuit = 0;
  let bestScore = Number.POSITIVE_INFINITY;
  for (let suit = 0; suit < 3; suit++) {
    const base = suit * 9;
    let n = 0;
    let pairs = 0;
    let conn = 0;
    for (let r = 0; r < 9; r++) {
      const c = counts[base + r] ?? 0;
      n += c;
      if (c >= 2) pairs++;
      if (r <= 7 && c > 0 && (counts[base + r + 1] ?? 0) > 0) conn++;
    }
    const score = n * 100 + pairs * 30 + conn * 20;
    if (score < bestScore) {
      bestScore = score;
      bestSuit = suit;
    }
  }
  return (['wan', 'tong', 'tiao'] as const)[bestSuit]!;
}

/** 升序插入（保持手牌有序，使序列化结果稳定）。 */
export function sortedInsert(hand: readonly TileId[], id: TileId): readonly TileId[] {
  const out = hand.slice();
  out.push(id);
  out.sort((a, b) => a - b);
  return out;
}

/** 移除一张牌；不存在时返回 `null`（调用方据此判 `tile_not_owned`）。 */
export function removeTile(hand: readonly TileId[], id: TileId): readonly TileId[] | null {
  const i = hand.indexOf(id);
  if (i < 0) return null;
  const out = hand.slice();
  out.splice(i, 1);
  return out;
}

/** 从手牌里取出指定牌面的 `n` 张实例 id（升序）；不足时返回 `null`。 */
export function takeKind(hand: readonly TileId[], kindIndex: number, n: number): readonly TileId[] | null {
  const picked: TileId[] = [];
  for (const id of hand) {
    if (kindIndexFromTileId(id) === kindIndex && picked.length < n) picked.push(id);
  }
  return picked.length === n ? picked.sort((a, b) => a - b) : null;
}
